import { useCallback, useEffect, useMemo, useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { selectAuth } from "shared/redux/slices/authSlice";

import {
  useChangeStatusNotificationV1Mutation,
  useReadAllNotificationMutation,
  useChangeStatusNotificationMutation,
  useLazyGetNotificationCountQuery,
  useLazyGetNotificationsQuery,
} from "shared/redux/endpoints/notifications";

import {
  changeStatusNotification,
  increasePageToday,
  addNewNotification,
  readAllNotifications,
  setNotificationDetail,
  updateNotificationsToday,
  updateNotifications,
  increasePage,
  selectNotifcation,
} from "shared/redux/slices/notificationSlice";

import { RoleLabel, Role } from "constant/Role";

export const useNotification = () => {
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreToday, setHasMoreToday] = useState(true);

  const [trigger] = useLazyGetNotificationsQuery();
  const auth = useSelector(selectAuth);

  const centerId = useMemo(() => {
    if (auth?.role === RoleLabel[Role.LXO] && auth?.centerId > 0)
      return auth?.centerId;
    return null;
  }, [auth]);

  const isLogged = useMemo(
    () => !!auth.accessToken && !!auth.refreshToken,
    [auth]
  );

  const { currentPageToday, currentPage, notifications, notificationsToday } =
    useSelector(selectNotifcation);

  const [notificationCount, setNotificationCount] = useState({});

  const [triggerCount] = useLazyGetNotificationCountQuery();

  const [triggerReadAll] = useReadAllNotificationMutation();

  const [changeStatus] = useChangeStatusNotificationMutation();
  const [changeStatusV1] = useChangeStatusNotificationV1Mutation();

  const dispatch = useDispatch();

  const params = {
    type: null,
    page: 0,
    ...(centerId && { centerId }),
    size: 10,
    sort: "created,desc",
  };
  // console.log({ fn: "useNotification", centerId, params });

  const fetchMore = useCallback(
    async (currentPage) => {
      const newParams = {
        ...params,
        type: "older",
        page: currentPage,
      };
      const response = await trigger(newParams);
      if (response && response?.data?.status && hasMore) {
        if (response.data.data.data.length === 0) {
          setHasMore(false);
        } else {
          dispatch(
            updateNotifications({
              data: response.data.data.data,
            })
          );
          dispatch(increasePage(currentPage + 1));
        }
      }
    },
    [hasMore]
  );

  const fetchMoreToday = useCallback(
    async (currentPage) => {
      const newParams = {
        ...params,
        type: "today",
        page: currentPage,
      };
      const responseToday = await trigger(newParams);

      if (responseToday && responseToday?.data?.status && hasMoreToday) {
        if (responseToday.data.data.data.length === 0) {
          setHasMoreToday(false);
        } else {
          dispatch(
            updateNotificationsToday({
              data: responseToday.data.data.data,
            })
          );
          dispatch(increasePageToday(currentPage + 1));
        }
      }
    },
    [hasMoreToday]
  );

  useEffect(() => {
    const initNoti = async () => {
      if (isLogged) {
        const count = await triggerCount();
        if (count.isSuccess) setNotificationCount(count.data);
        fetchMore(0);
        fetchMoreToday(0);
      }
    };
    initNoti();
  }, [isLogged, auth.user.email]);

  const fetchAll = async (data) => {
    try {
      dispatch(
        addNewNotification({
          newNotification: data,
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const count = await triggerCount();
      if (count.isSuccess) {
        setNotificationCount(count.data);
      }
    } catch (error) {
      console.error("Error in fetchAll:", error);
    }
  };

  const handleChangeStatus = async (
    notificationId,
    uniqueContentId,
    read,
    type
  ) => {
    dispatch(
      changeStatusNotification({
        notificationId,
        uniqueContentId,
        read,
        type,
      })
    );
    if (uniqueContentId) {
      await changeStatus({
        uniqueContentId,
        read,
      });
    } else {
      await changeStatusV1({
        notificationId,
        read,
      });
    }

    await triggerCount();
  };

  const handleSetNotificationDetail = (notification) => {
    dispatch(
      setNotificationDetail({
        notificationDetail: notification,
      })
    );
  };

  const handlerReadAllNotification = async () => {
    dispatch(readAllNotifications());
    await triggerReadAll();
    setTimeout(async () => {
      await triggerCount();
      //await trigger();
    }, 500);
  };

  return {
    currentPage,
    currentPageToday,
    fetchAll,
    fetchMore,
    fetchMoreToday,
    handleChangeStatus,
    handleSetNotificationDetail,
    hasMore,
    hasMoreToday,
    notifications,
    notificationsToday,
    notificationCount,
    handlerReadAllNotification,
  };
};
